<template>
  <div class="flex-1 overflow-y-auto">
    <!-- 添加模块按钮 -->
    <div class="p-3 border-b border-gray-100 flex items-center justify-between">
      <span class="text-xs font-semibold text-gray-500 uppercase">页面模块</span>
      <div class="relative" ref="addMenuRef">
        <button @click="showAddMenu = !showAddMenu" class="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
          添加模块
        </button>
        <div v-if="showAddMenu" class="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 max-h-72 overflow-y-auto">
          <div v-for="bt in blockTypes" :key="bt.type" class="flex items-center gap-1 px-2 py-1.5 hover:bg-gray-50 group">
            <button type="button" @click="addBlock(bt.type)" class="flex-1 flex items-center gap-2 text-left text-xs text-gray-700 min-w-0 py-0.5">
              <span class="flex-shrink-0">{{ bt.icon }}</span>
              <span class="truncate">{{ bt.label }}</span>
            </button>
            <ConfigHelpButton :help-key="bt.helpKey" size="sm" />
          </div>
        </div>
      </div>
    </div>
    <!-- 拖拽列表 -->
    <draggable
      :list="blocks"
      item-key="id"
      handle=".drag-handle"
      ghost-class="opacity-30"
      @end="onDragEnd"
      class="divide-y divide-gray-50"
    >
      <template #item="{ element, index }">
        <div class="group" :class="{ 'bg-blue-50': expandedBlock === element.id }">
          <!-- 区块头 -->
          <div class="flex items-center gap-2 px-3 py-2 cursor-pointer" @click="toggleExpand(element.id)">
            <span class="drag-handle cursor-grab text-gray-300 hover:text-gray-500">
              <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><circle cx="9" cy="5" r="1.5"/><circle cx="15" cy="5" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="9" cy="19" r="1.5"/><circle cx="15" cy="19" r="1.5"/></svg>
            </span>
            <span class="text-xs">{{ getBlockIcon(element.type) }}</span>
            <span class="text-xs font-medium text-gray-700 flex-1 truncate inline-flex items-center gap-1 min-w-0">
              <span class="truncate">{{ getBlockLabel(element.type) }}</span>
              <ConfigHelpButton :help-key="getBlockHelpKey(element.type)" size="sm" />
            </span>
            <button @click.stop="element.visible = !element.visible; emitUpdate()" :title="element.visible ? '隐藏' : '显示'"
              :class="element.visible ? 'text-blue-500' : 'text-gray-300'" class="p-0.5">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="element.visible ? 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' : 'M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18'" /></svg>
            </button>
            <button v-if="canDelete(element)" @click.stop="removeBlock(index)" class="p-0.5 text-gray-300 hover:text-red-500">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3"/></svg>
            </button>
            <svg class="w-3 h-3 text-gray-400 transition-transform" :class="{ 'rotate-180': expandedBlock === element.id }" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
          </div>
          <!-- 区块配置面板 -->
          <div v-if="expandedBlock === element.id" class="px-3 pb-3 space-y-2">
            <!-- 间距控制（自定义HTML整页直出，间距无意义，故隐藏） -->
            <div v-if="element.type !== 'richtext_full'" class="flex items-center gap-2">
              <ConfigHelpLabel label="间距" help-key="block_margin" label-class="text-[10px] text-gray-500 w-10 flex-shrink-0" tag="span" />
              <input type="range" v-model.number="element.margin" min="0" max="60" step="2" class="flex-1 h-1 accent-blue-600" @input="emitUpdate()" />
              <span class="text-[10px] text-gray-400 w-6 text-right">{{ element.margin }}px</span>
            </div>
            <!-- 各区块专属配置 -->
            <component :is="getConfigComponent(element.type)" :block="element" :lang-content="langContent" :page-id="pageId" :page="page" @update="emitUpdate" />
          </div>
        </div>
      </template>
    </draggable>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import draggable from 'vuedraggable'
import BlockConfigHeading from './blocks/BlockConfigHeading.vue'
import BlockConfigText from './blocks/BlockConfigText.vue'
import BlockConfigImage from './blocks/BlockConfigImage.vue'
import BlockConfigForm from './blocks/BlockConfigForm.vue'
import BlockConfigButtons from './blocks/BlockConfigButtons.vue'
import BlockConfigVideo from './blocks/BlockConfigVideo.vue'
import BlockConfigFaq from './blocks/BlockConfigFaq.vue'
import BlockConfigSocial from './blocks/BlockConfigSocial.vue'
import BlockConfigRichtext from './blocks/BlockConfigRichtext.vue'
import BlockConfigBanner from './blocks/BlockConfigBanner.vue'
import BlockConfigSpacer from './blocks/BlockConfigSpacer.vue'
import BlockConfigDivider from './blocks/BlockConfigDivider.vue'
import BlockConfigRating from './blocks/BlockConfigRating.vue'
import ConfigHelpLabel from './ConfigHelpLabel.vue'
import ConfigHelpButton from './ConfigHelpButton.vue'

const MODULE_HELP_KEYS = {
  heading: 'module_heading',
  text: 'module_text',
  image: 'module_image',
  banner: 'module_banner',
  form: 'module_form',
  buttons: 'module_buttons',
  video: 'module_video',
  faq: 'module_faq',
  richtext: 'module_richtext',
  richtext_full: 'module_richtext_full',
  social: 'module_social',
  divider: 'module_divider',
  spacer: 'module_spacer',
  rating: 'module_rating',
}

function withHelpKey(items) {
  return items.map((item) => ({ ...item, helpKey: MODULE_HELP_KEYS[item.type] }))
}

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  langContent: { type: Object, default: () => ({}) },
  pageId: { type: [Number, String], default: null },
  page: { type: [Number, String], default: 1 }
})
const emit = defineEmits(['update:modelValue'])

const blocks = computed({ get: () => props.modelValue || [], set: (v) => emit('update:modelValue', v) })
const showAddMenu = ref(false)
const expandedBlock = ref(null)
const addMenuRef = ref(null)

const isP2 = computed(() => String(props.page) === '2')
// 第一页锁定订阅表单不可删；第二页锁定评星模块不可删
const lockedType = computed(() => isP2.value ? 'rating' : 'form')

const BLOCK_TYPES_P1 = withHelpKey([
  { type: 'heading', icon: '📝', label: '标题' },
  { type: 'text', icon: '📄', label: '段落文字' },
  { type: 'image', icon: '🖼️', label: '图片' },
  { type: 'banner', icon: '🎞️', label: '横幅大图' },
  { type: 'form', icon: '📋', label: '订阅表单' },
  { type: 'buttons', icon: '🔗', label: '联系按钮' },
  { type: 'video', icon: '🎬', label: 'YouTube 视频' },
  { type: 'faq', icon: '❓', label: 'FAQ 问答' },
  { type: 'richtext', icon: '✏️', label: '富文本（卡片内）' },
  { type: 'richtext_full', icon: '📰', label: '自定义HTML' },
  { type: 'social', icon: '📱', label: '社交媒体' },
  { type: 'divider', icon: '➖', label: '分割线' },
  { type: 'spacer', icon: '↕️', label: '空白间隔' },
])
const BLOCK_TYPES_P2 = withHelpKey([
  { type: 'rating', icon: '⭐', label: '评星模块' },
  { type: 'heading', icon: '📝', label: '标题' },
  { type: 'text', icon: '📄', label: '段落文字' },
  { type: 'image', icon: '🖼️', label: '图片' },
  { type: 'banner', icon: '🎞️', label: '横幅大图' },
  { type: 'video', icon: '🎬', label: 'YouTube 视频' },
  { type: 'faq', icon: '❓', label: 'FAQ 问答' },
  { type: 'richtext', icon: '✏️', label: '富文本（卡片内）' },
  { type: 'richtext_full', icon: '📰', label: '自定义HTML' },
  { type: 'social', icon: '📱', label: '社交媒体' },
  { type: 'divider', icon: '➖', label: '分割线' },
  { type: 'spacer', icon: '↕️', label: '空白间隔' },
])
const blockTypes = computed(() => (isP2.value ? BLOCK_TYPES_P2 : BLOCK_TYPES_P1).filter(bt => bt.type !== lockedType.value))
const ALL_TYPES = [...BLOCK_TYPES_P1, { type: 'rating', icon: '⭐', label: '评星模块' }]

// 锁定的核心模块（第一页订阅表单/第二页评星）保持唯一：
// 删除按钮仅在「非锁定类型」或「同类锁定模块存在多个」时显示，
// 以便清理历史重复项，同时始终至少保留一个。
function canDelete(element) {
  if (element.type !== lockedType.value) return true
  return blocks.value.filter(b => b.type === lockedType.value).length > 1
}

function genId() { return 'b' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6) }
function getBlockIcon(type) { return (ALL_TYPES.find(b => b.type === type) || {}).icon || '📦' }
function getBlockLabel(type) { return (ALL_TYPES.find(b => b.type === type) || {}).label || type }
function getBlockHelpKey(type) { return MODULE_HELP_KEYS[type] || '' }

function getConfigComponent(type) {
  const map = { heading: BlockConfigHeading, text: BlockConfigText, image: BlockConfigImage, form: BlockConfigForm, buttons: BlockConfigButtons, video: BlockConfigVideo, faq: BlockConfigFaq, social: BlockConfigSocial, richtext: BlockConfigRichtext, richtext_full: BlockConfigRichtext, banner: BlockConfigBanner, spacer: BlockConfigSpacer, divider: BlockConfigDivider, rating: BlockConfigRating }
  return map[type] || BlockConfigSpacer
}

function addBlock(type) {
  const newBlock = { id: genId(), type, visible: true, margin: 14, data: {} }
  if (type === 'spacer') newBlock.data = { height: 20 }
  if (type === 'faq') newBlock.data = { title: 'FAQ', items: [] }
  if (type === 'social') newBlock.data = { links: [] }
  if (type === 'rating') {
    const lc = props.langContent || {}
    newBlock.data = {
      activate_title: lc.activate_title || '', rate_prompt: lc.rate_prompt || '',
      review_prompt: lc.review_prompt || '', review_button: lc.review_button || '',
      low_rate_prompt: lc.low_rate_prompt || '', countdown_text: lc.countdown_text || ''
    }
  }
  blocks.value.push(newBlock)
  showAddMenu.value = false
  expandedBlock.value = newBlock.id
  emitUpdate()
}
function removeBlock(idx) { blocks.value.splice(idx, 1); emitUpdate() }
function toggleExpand(id) { expandedBlock.value = expandedBlock.value === id ? null : id }
function onDragEnd() { emitUpdate() }
function emitUpdate() { emit('update:modelValue', [...blocks.value]) }

// 点击外部关闭菜单
function handleClickOutside(e) { if (addMenuRef.value && !addMenuRef.value.contains(e.target)) showAddMenu.value = false }
onMounted(() => document.addEventListener('click', handleClickOutside))
onBeforeUnmount(() => document.removeEventListener('click', handleClickOutside))
</script>
